<template>
  <button
    type="button"
    class="deck-card-sprite"
    :class="{ 'just-added': pulse, holding, 'is-fluid': fluid, 'materials-ready': materialsReady }"
    :title="title"
    @mouseenter="$emit('hover')"
    @pointerdown="onPointerDown"
    @click="onClick"
    @contextmenu.prevent="onContextMenu"
  >
    <YugiohCard :card="card" size="tiny" :fluid="fluid" :clickable="false" />
    <div v-if="!hideHud" class="sprite-hud" aria-hidden="true">
      <span v-if="isMonster && card.level" class="sprite-level">
        &#9733;{{ card.level }}
      </span>
      <span v-if="isMonster && card.atk != null" class="sprite-stats">
        <span class="atk">{{ card.atk }}</span>
        <span class="sep">/</span>
        <span class="def">{{ card.def }}</span>
      </span>
      <span v-else-if="!isMonster" class="sprite-kind">{{ kindLabel }}</span>
    </div>
    <span
      v-if="pvpLimit != null"
      class="sprite-pvp-limit"
      :class="{ 'with-qty': !!qty }"
      :title="t('deck.pvpLimitBadge', { n: pvpLimit })"
      aria-hidden="true"
    >{{ pvpLimit }}</span>
    <span v-if="qty" class="sprite-qty">{{ qty }}</span>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useSiteStore } from '../stores/site'
import YugiohCard from './YugiohCard.vue'
import { useHoldToFill } from '../composables/useHoldToFill'
import { localizedName } from '../i18n/localizeCard'
import { useI18n } from '../i18n/t'

const props = defineProps<{
  card: any
  pulse?: boolean
  qty?: string
  hideHud?: boolean
  fillOnHold?: boolean
  /** Fill the grid cell instead of the fixed 80px thumb. */
  fluid?: boolean
  /** Fusion whose materials are already in the main deck. */
  materialsReady?: boolean
  /** Skip the house-banlist badge. */
  hideLimit?: boolean
}>()

const emit = defineEmits<{
  hover: []
  click: [src: HTMLElement]
  fill: [src: HTMLElement]
  contextmenu: [event: MouseEvent]
}>()

const { holding, begin, consumeClick, consumeContext } = useHoldToFill((el) => {
  emit('fill', el)
})

function onPointerDown(event: PointerEvent): void {
  emit('hover')
  if (!props.fillOnHold) return
  begin(event)
}

function onClick(event: MouseEvent): void {
  if (consumeClick()) return
  const el = event.currentTarget as HTMLElement | null
  if (!el) return
  emit('click', el)
}

function onContextMenu(event: MouseEvent): void {
  const action = consumeContext()
  if (action === 'fill' || action === 'skip') return
  emit('contextmenu', event)
}

const { t } = useI18n()

const site = useSiteStore()
const pvpLimit = computed(() => {
  if (props.hideLimit) return null
  const id = String(props.card?.id ?? '')
  const max = site.houseLimits[id]
  return max === undefined ? null : max
})

const isMonster = computed(() => {
  const card = props.card
  if (Array.isArray(card?.typeline) && card.typeline.includes('Monster')) return true
  return String(card?.type || '').toLowerCase().includes('monster')
})

const isSpell = computed(() => {
  const card = props.card
  if (Array.isArray(card?.typeline) && card.typeline.includes('Spell')) return true
  return String(card?.type || '').toLowerCase().includes('spell')
})

const kindLabel = computed(() => {
  if (isSpell.value) return t('deck.spell')
  return t('deck.trap')
})

const title = computed(() => {
  const name = localizedName(props.card?.id, props.card?.name || '')
  if (props.fillOnHold) return `${name} — ${t('deck.holdToAddThree')}`
  return name
})
</script>

<style scoped>
.deck-card-sprite {
  position: relative;
  display: block;
  width: 80px;
  flex: 0 0 80px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  line-height: 0;
  overflow: visible;
  transition: transform 0.15s ease;
  user-select: none;
  touch-action: pan-y;
  -webkit-touch-callout: none;
}

.deck-card-sprite.is-fluid {
  width: 100%;
  flex: none;
  aspect-ratio: 80 / 117;
}

.deck-card-sprite.holding {
  transform: scale(0.94);
}

.deck-card-sprite:hover {
  transform: scale(1.08);
  z-index: 10;
}

.deck-card-sprite.materials-ready::after {
  content: '';
  position: absolute;
  inset: -2px;
  border: 2px solid light-dark(#166534, #4ade80);
  border-radius: 5px;
  pointer-events: none;
}

.deck-card-sprite.just-added {
  animation: sprite-land 480ms cubic-bezier(0.2, 0, 0, 1);
  z-index: 8;
}

@keyframes sprite-land {
  0% {
    transform: scale(0.55);
    opacity: 0.35;
  }
  55% {
    transform: scale(1.12);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .deck-card-sprite.just-added {
    animation: none;
  }
}

.sprite-hud {
  position: absolute;
  inset: 0;
  pointer-events: none;
  border-radius: 4px;
  overflow: hidden;
}

.sprite-level {
  position: absolute;
  top: 2px;
  left: 2px;
  padding: 0 3px;
  border-radius: 3px;
  background: rgba(0, 0, 0, 0.62);
  color: #facc15;
  font-size: 9px;
  font-weight: 800;
  line-height: 1.35;
  letter-spacing: 0.02em;
  text-shadow: 0 1px 2px #000;
}

.sprite-stats,
.sprite-kind {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 10px 1px 3px;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.88) 55%);
  text-align: center;
  font-size: 8px;
  font-weight: 700;
  line-height: 1.15;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.sprite-kind {
  color: #e5e7eb;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.atk {
  color: #f87171;
}

.sep {
  color: #9ca3af;
  margin: 0 1px;
}

.def {
  color: #60a5fa;
}

.sprite-qty {
  position: absolute;
  top: -4px;
  right: -4px;
  z-index: 2;
  min-width: 22px;
  padding: 1px 5px;
  border-radius: 999px;
  background: #e8c547;
  color: #1c1b1f;
  font-size: 10px;
  font-weight: 800;
  line-height: 1.35;
  font-variant-numeric: tabular-nums;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.45);
}

.sprite-pvp-limit {
  position: absolute;
  top: 2px;
  right: 2px;
  z-index: 2;
  min-width: 18px;
  padding: 1px 5px;
  border-radius: 999px;
  background: #b3261e;
  color: #fff;
  font-size: 10px;
  font-weight: 800;
  line-height: 1.35;
  text-align: center;
  font-variant-numeric: tabular-nums;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.45);
}

.sprite-pvp-limit.with-qty {
  right: auto;
  left: 2px;
}
</style>
